import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HypothesesHypothesisProgramDoor as HypothesisProgramDoor,
  HypothesesHypothesisProgramList as HypothesisProgramList,
  HypothesesHypothesisProgramRef as HypothesisProgramRef,
  HypothesesHypothesisProgramReport as HypothesisProgramReport,
  HypothesesProgramDoorLock as ProgramDoorLock,
  HypothesesProgramHypothesisRef as ProgramHypothesisRef,
} from '../generated/api/types.gen.ts';
import { hypothesesCreateProgram, hypothesesListPrograms } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { hypoteseNoekkel } from './useHypotheses.ts';
import { programNoekkel, type Program, type ProgramCreate } from './usePrograms.ts';

export type { HypothesisProgramDoor, HypothesisProgramList, HypothesisProgramRef, HypothesisProgramReport, ProgramDoorLock, ProgramHypothesisRef };

/**
 * H3.4 — betaprogram fra hypotesen, og rapporten som forslag til konklusjonen.
 *
 * Programmene og døren hentes i ett kall (`GET /hypotheses/{id}/programs`): detaljen viser
 * seksjonen «Betaprogram» og døren, konklusjonen tilbyr de fryste rapportene. Hva hver rad bærer,
 * er trimmet av tjeneren etter kallerens tilgang til Innspill (P7) — uten den kommer tall og
 * konklusjon, aldri navn, produkt eller tekst.
 *
 * Forhåndsutfyllingen er en KLIENTREGEL (`programmer/fraHypotese.ts`): det finnes ikke noe
 * endepunkt som gir et utkast, så ingenting finnes på tjeneren før eieren lagrer.
 *
 * Nøkkelen eies her og bare her (`react-ts-integration`). Den deler prefikset `hypotheses`, så en
 * overgang som friskere opp hele rommet, friskere også programmene.
 */
export const hypoteseProgramNoekkel = {
  liste: (id: string) => ['hypotheses', 'programmer', id] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Programmene laget fra hypotesen, og døren for den innloggede. Skriver ingen logg-rad (Kh9). */
export function useHypoteseProgrammer(id: string | null, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: hypoteseProgramNoekkel.liste(id ?? ''),
    enabled: enabled && id !== null,
    queryFn: async (): Promise<HypothesisProgramList> => {
      const res = await hypothesesListPrograms({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisProgramList;
    },
  });
}

/**
 * «Lagre utkast» i skjemaet som ble åpnet fra hypotesen (`POST /hypotheses/{id}/programs`). Først
 * her finnes programmet — og lenken til hypotesen, som aldri endres etterpå.
 */
export function useLagProgramFraHypotese() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ hypoteseId, body }: { hypoteseId: string; body: ProgramCreate }): Promise<Program> => {
      const res = await hypothesesCreateProgram({ client: klient, path: { id: hypoteseId }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as Program;
    },
    onSuccess: (program, { hypoteseId }) => {
      queryClient.setQueryData(programNoekkel.detalj(program.id), program);
      void queryClient.invalidateQueries({ queryKey: programNoekkel.alle });
      void queryClient.invalidateQueries({ queryKey: hypoteseProgramNoekkel.liste(hypoteseId) });
      void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.detalj(hypoteseId) });
    },
  });
}
